import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FormFeedback } from '@/components/ui/form-feedback';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { DashboardFiltersCard } from './dashboard/DashboardFiltersCard';
import { BaselineImportCard, DashboardKpis } from './dashboard/DashboardSummaryCards';
import { MonthlySeriesCard } from './dashboard/MonthlySeriesCard';
import { MovementsCard } from './dashboard/MovementsCard';
import { useDashboardData } from './dashboard/useDashboardData';
import { useForecastForm } from './dashboard/useForecastForm';
import { FINANCE_YEARS, resolveFinanceLocale, type FinanceDemoYear } from './finance-format';
import { useFinanceCapability } from './useFinanceCapability';

function YearSelector({
  selectedYear,
  onSelect,
}: {
  selectedYear: FinanceDemoYear;
  onSelect: (year: FinanceDemoYear) => void;
}) {
  const { t } = useTranslation('finance');

  // `role="group"`, not `tablist`: a tablist's children must carry `role="tab"` and be driven
  // by arrow keys, while these are toggle buttons reporting `aria-pressed`. Declaring a
  // tablist over `aria-pressed` buttons makes assistive technology announce a tab set whose
  // tabs it cannot find (WCAG 2.2 SC 4.1.2).

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('dashboard.yearSelector')}>
      {FINANCE_YEARS.map((year) => (
        <Button
          key={year}
          type="button"
          variant={selectedYear === year ? 'default' : 'outline'}
          aria-pressed={selectedYear === year}
          onClick={() => onSelect(year)}
        >
          {year}
        </Button>
      ))}
    </div>
  );
}

export function FinanceDashboardPage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage ?? i18n.language);
  const capability = useFinanceCapability({ requireActiveContext: true });
  const [selectedYear, setSelectedYear] = useState<FinanceDemoYear>(2026);
  const data = useDashboardData(selectedYear);
  const { state, load } = data;
  const forecastForm = useForecastForm({ locale, selectedYear, reload: load });
  const canWriteForecast = capability.status === 'allowed' && capability.canWriteForecast;

  // Both lists come from the server, computed over the UNFILTERED year. Derived from the rows just
  // received they would collapse to the option already chosen, and the reader could never widen the
  // selection again.
  const availableCategories = state.status === 'ready' ? state.availableCategories : [];
  const availableProjectLabels = state.status === 'ready' ? state.availableProjectLabels : [];
  const refreshing = state.status === 'ready' && state.refreshing;
  const hasActiveFilter =
    data.directionFilter !== 'all' ||
    data.statusFilter !== 'all' ||
    data.categoryFilter !== 'all' ||
    data.projectLabelFilter !== 'all';
  // The server has already narrowed these rows; filtering them again here would be the very pass this
  // activity removed.
  const visibleMovements = state.status === 'ready' ? state.movements : [];

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h2 className="screen-title">{t('dashboard.title')}</h2>
        <p className="screen-summary">{t('dashboard.subtitle')}</p>
      </header>

      <YearSelector selectedYear={selectedYear} onSelect={setSelectedYear} />

      {/* The year selector above stays mounted through the load, so the skeleton stands in for the
          figures only — replacing the whole screen would take the control that triggered the
          reload away from under the pointer that just used it. */}
      {state.status === 'loading' && <CardsSkeleton />}

      {state.status === 'error' && (
        <div className="space-y-3">
          <FormFeedback tone="error">{t('dashboard.loadError')}</FormFeedback>
          <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
            {t('dashboard.retry')}
          </Button>
        </div>
      )}

      {state.status === 'ready' && (
        <>
          <DashboardKpis dashboard={state.dashboard} selectedYear={selectedYear} locale={locale} />
          <BaselineImportCard baselineImport={state.dashboard.baselineImport} locale={locale} />

          <section className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
            <MonthlySeriesCard
              monthlySeries={state.dashboard.monthlySeries}
              selectedYear={selectedYear}
              hasActiveFilter={hasActiveFilter}
              locale={locale}
            />
            <DashboardFiltersCard
              forecastForm={canWriteForecast ? forecastForm : null}
              directionFilter={data.directionFilter}
              onDirectionChange={data.setDirectionFilter}
              statusFilter={data.statusFilter}
              onStatusChange={data.setStatusFilter}
              categoryFilter={data.categoryFilter}
              availableCategories={availableCategories}
              onCategoryChange={data.setCategoryFilter}
              projectLabelFilter={data.projectLabelFilter}
              availableProjectLabels={availableProjectLabels}
              onProjectLabelChange={data.setProjectLabelFilter}
            />
          </section>

          <MovementsCard
            movements={visibleMovements}
            selectedYear={selectedYear}
            refreshing={refreshing}
            hasActiveFilter={hasActiveFilter}
            canWriteForecast={canWriteForecast}
            locale={locale}
            onEditForecast={forecastForm.openEdit}
          />
        </>
      )}
    </div>
  );
}
